import { morph, MORPH_OPTIONS } from '@theme/morph';
class SectionRenderer {
  #cache = new Map();
  #abortControllersBySectionId = new Map();
  #pendingRendersBySectionId = new Map();
  #pendingPromises = new Map();
  constructor() {
    window.addEventListener('load', this.#cachePageSections.bind(this));
  }
  /**
   * Re-render a section, morphing the result into the live DOM.
   *
   * @param {string} sectionId Target section id.
   * @param {object} [options] Render options.
   * @param {boolean} [options.cache=true] When false, bypass the cached response.
   * @param {'full' | 'hydration'} [options.mode='full'] Morph mode to apply.
   * @param {boolean} [options.injectStylesheet=false] Whether to inject the section stylesheet.
   * @param {URL} [options.url] URL used to build the rendering request.
   * @returns {Promise<string>} Resolves with the section HTML that was rendered.
   */ async renderSection(sectionId, options) {
    const {
      cache: cache = !Shopify.designMode,
      mode: mode = 'full',
      injectStylesheet: injectStylesheet = false,
      url: url,
    } = options ?? {};
    this.#abortPendingMorph(sectionId);
    const abortController = new AbortController();
    this.#abortControllersBySectionId.set(sectionId, abortController);
    const renderPromise = this.#renderSection(
      sectionId,
      {
        cache: cache,
        mode: mode,
        injectStylesheet: injectStylesheet,
        url: url,
      },
      abortController,
    );
    this.#pendingRendersBySectionId.set(sectionId, {
      abortController: abortController,
      promise: renderPromise,
    });
    return renderPromise;
  }
  async #renderSection(
    sectionId,
    { cache: cache, mode: mode, injectStylesheet: injectStylesheet, url: url },
    abortController,
  ) {
    let sectionHTML = '';
    try {
      sectionHTML = await this.getSectionHTML(
        sectionId,
        cache,
        url,
        abortController.signal,
      );
    } catch (error) {
      if (abortController.signal.aborted) {
        const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
        if (
          pendingRender &&
          pendingRender.abortController !== abortController
        ) {
          return pendingRender.promise;
        }
        return sectionHTML;
      }
      throw error;
    } finally {
      if (
        this.#abortControllersBySectionId.get(sectionId) === abortController
      ) {
        this.#abortControllersBySectionId.delete(sectionId);
      }
      const pendingRender = this.#pendingRendersBySectionId.get(sectionId);
      if (pendingRender?.abortController === abortController) {
        this.#pendingRendersBySectionId.delete(sectionId);
      }
    }
    if (!abortController.signal.aborted) {
      morphSection(sectionId, sectionHTML, {
        mode: mode,
        injectStylesheet: injectStylesheet,
      });
    }
    return sectionHTML;
  }
  #abortPendingMorph(sectionId) {
    const existingAbortController =
      this.#abortControllersBySectionId.get(sectionId);
    if (existingAbortController) {
      existingAbortController.abort();
      this.#abortControllersBySectionId.delete(sectionId);
    }
  }
  async getSectionHTML(
    sectionId,
    useCache = true,
    url = new URL(window.location.href),
    signal,
  ) {
    const sectionUrl = buildSectionRenderingURL(sectionId, url);
    const shouldSharePendingPromise = !signal;
    let pendingPromise = this.#pendingPromises.get(sectionUrl);
    if (pendingPromise) return pendingPromise;
    if (useCache) {
      const cachedHTML = this.#cache.get(sectionUrl);
      if (cachedHTML) return cachedHTML;
    }
    pendingPromise = fetch(sectionUrl, { signal: signal }).then((response) => {
      if (!response.ok)
        throw new Error(`section render failed: ${response.status}`);
      return response.text();
    });
    if (shouldSharePendingPromise) {
      pendingPromise = pendingPromise.finally(() => {
        this.#pendingPromises.delete(sectionUrl);
      });
      this.#pendingPromises.set(sectionUrl, pendingPromise);
    }
    const sectionHTML = await pendingPromise;
    this.#cache.set(sectionUrl, sectionHTML);
    return sectionHTML;
  }
  #cachePageSections() {
    for (const section of document.querySelectorAll('.shopify-section')) {
      const url = buildSectionRenderingURL(section.id);
      if (this.#cache.get(url)) continue;
      if (containsShadowRoot(section)) continue;
      this.#cache.set(url, section.outerHTML);
    }
  }
}
const SECTION_ID_PREFIX = 'shopify-section-';
function buildSectionRenderingURL(
  sectionId,
  url = new URL(window.location.href),
) {
  url.searchParams.set('section_id', normalizeSectionId(sectionId));
  url.searchParams.sort();
  return url.toString();
}
/**
 * Build the DOM selector that targets a Shopify section by id.
 *
 * @param {string} sectionId Section id (with or without the `shopify-section-` prefix).
 * @returns {string} The full DOM selector.
 */ function buildSectionSelector(sectionId) {
  return `${SECTION_ID_PREFIX}${sectionId}`;
}
/**
 * Strip the `shopify-section-` prefix from a section id, if present.
 *
 * @param {string} sectionId Section id.
 * @returns {string} Normalized section id without the prefix.
 */ function normalizeSectionId(sectionId) {
  return sectionId.replace(new RegExp(`^${SECTION_ID_PREFIX}`), '');
}
function containsShadowRoot(element) {
  return (
    !!element.shadowRoot ||
    Array.from(element.children).some(containsShadowRoot)
  );
}
/**
 * Replace the live section element with new HTML, using a morph-based update.
 *
 * @param {string} sectionId Section id whose live element should be morphed.
 * @param {string} html The new section HTML, parsed as a document.
 * @param {object} [options] Morph options.
 * @param {'full' | 'hydration'} [options.mode='full'] Morph mode to apply.
 * @param {boolean} [options.injectStylesheet=false] Whether to inject the section stylesheet.
 * @returns {Promise<void>} Resolves when the morph completes.
 */ async function morphSection(sectionId, html, options = {}) {
  const { mode: mode = 'full', injectStylesheet: injectStylesheet = false } =
    options;
  const fragment = new DOMParser().parseFromString(html, 'text/html');
  const existingElement = document.getElementById(
    buildSectionSelector(sectionId),
  );
  const newElement = fragment.getElementById(buildSectionSelector(sectionId));
  if (!existingElement) {
    throw new Error(`Section ${sectionId} not found`);
  }
  if (!newElement) {
    throw new Error(
      `Section ${sectionId} not found in the section rendering response`,
    );
  }
  morph(existingElement, newElement, {
    ...MORPH_OPTIONS,
    hydrationMode: mode === 'hydration',
  });
  if (injectStylesheet) {
    injectSectionStylesheet(fragment, existingElement);
  }
}
function injectSectionStylesheet(fragment, sectionElement) {
  const newStylesheet = fragment.querySelector(
    'style[data-section-stylesheet]',
  );
  if (!newStylesheet) return;
  const existingStylesheet = sectionElement.querySelector(
    'style[data-section-stylesheet]',
  );
  if (existingStylesheet) {
    existingStylesheet.textContent = newStylesheet.textContent;
  } else {
    sectionElement.prepend(newStylesheet);
  }
}
export const sectionRenderer = new SectionRenderer();
